'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { BrandLogo } from '@/components/brand-logo';
import { Menu, X, ArrowRight, UserPlus, LogIn, Search } from 'lucide-react';
import type { SiteContent, Locale } from '@/lib/content';
import { cn, homeHref } from '@/lib/utils';
import { LanguageSwitcher } from '@/components/language-switcher';
import { NavIcon, NAV_TINTS } from '@/components/nav-icons';


interface HeaderProps {
  locale: Locale;
  navigation: SiteContent['navigation'];
  primaryCta: SiteContent['hero']['primaryCta'];
  header: SiteContent['header'];
}

type NavChild = { label: string; href: string; icon?: string; group?: string };

/** A nav item earns the wide panel by grouping its children; a flat list keeps the old column. */
function hasGroups(item: unknown): boolean {
  const children = (item as { children?: NavChild[] }).children;

  return Array.isArray(children) && children.some((child) => Boolean(child.group));
}

/**
 * Children in the order their groups first appear, so the panel's columns follow
 * the order the content file states rather than an alphabetical one.
 *
 * @returns [group title, its children][]
 */
function groupChildren(item: unknown): Array<[string, NavChild[]]> {
  const children = ((item as { children?: NavChild[] }).children ?? []);
  const groups = new Map<string, NavChild[]>();

  for (const child of children) {
    const key = child.group ?? '';
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(child);
    } else {
      groups.set(key, [child]);
    }
  }

  return [...groups.entries()];
}

export function Header({ locale, navigation, primaryCta, header }: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buildHref = (href: string) => {
    if (!href) return `/${locale}/`;
    if (href.startsWith('/')) return href;
    const anchor = href.startsWith('#') ? href.slice(1) : href;
    return `/${locale}/#${anchor}`;
  };
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpenIndex(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 glass will-change-transform border-b border-white/50">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-white">
        Spring naar hoofdinhoud
      </a>
      <div className="container-wide py-4">
        {/* Desktop Layout */}
        <div className="hidden lg:flex items-center justify-between">
          <Link
            href={homeHref(locale)}
            className="flex items-center gap-3 text-base font-semibold text-slate-900"
            aria-label="Winkel Factuur startpagina"
          >
            <BrandLogo variant="full" />
          </Link>
          <nav className="relative flex items-center gap-6" aria-label="Hoofdnavigatie" ref={menuRef}>
            {navigation.map((item, index) => (
              <div
                key={item.href}
                className="group relative"
                onMouseEnter={() => {
                  if (closeTimeoutRef.current) {
                    clearTimeout(closeTimeoutRef.current);
                    closeTimeoutRef.current = null;
                  }
                  setOpenIndex(index);
                }}
                onMouseLeave={() => {
                  if (closeTimeoutRef.current) {
                    clearTimeout(closeTimeoutRef.current);
                  }
                  closeTimeoutRef.current = setTimeout(() => {
                    setOpenIndex((idx) => (idx === index ? null : idx));
                  }, 120);
                }}
              >
                {Array.isArray((item as any).children) && (item as any).children.length > 0 ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 transition hover:text-brand-700 focus:outline-none"
                    aria-haspopup="true"
                    aria-expanded={openIndex === index}
                    aria-label={`${item.label} submenu`}
                    onClick={() => setOpenIndex((idx) => (idx === index ? null : index))}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        setOpenIndex(index);
                        const el = (menuRef.current?.querySelector(`#submenu-${index} a`) ?? null) as HTMLAnchorElement | null;
                        el?.focus();
                      }
                    }}
                  >
                    {item.label}
                  </button>
                ) : (
                  <a
                    href={buildHref((item as any).href)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 transition hover:text-brand-700"
                  >
                    {item.label}
                  </a>
                )}
                {Array.isArray((item as any).children) && (item as any).children.length > 0 && (
                  <div
                    id={`submenu-${navigation.indexOf(item)}`}
                    role="menu"
                    aria-label={`${item.label} opties`}
                    className={cn(
                      'absolute left-0 top-full z-50 mt-3 rounded-2xl border border-slate-100 bg-white shadow-lg transition-opacity duration-150',
                      // Grouped children get the wide panel; a flat list keeps the
                      // narrow column it always had, so a nav item without groups
                      // still renders correctly.
                      hasGroups(item) ? 'w-[720px] p-5' : 'w-[280px] p-2',
                      openIndex === index ? 'visible opacity-100' : 'invisible opacity-0'
                    )}
                    onMouseEnter={() => {
                      if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                        closeTimeoutRef.current = null;
                      }
                      setOpenIndex(index);
                    }}
                    onMouseLeave={() => {
                      if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                      }
                      closeTimeoutRef.current = setTimeout(() => {
                        setOpenIndex((idx) => (idx === index ? null : idx));
                      }, 120);
                    }}
                  >
                    {hasGroups(item) ? (
                      <div className="grid grid-cols-3 gap-x-7 gap-y-5">
                        {groupChildren(item).map(([group, children]) => (
                          <div key={group}>
                            <p className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400">{group}</p>
                            <div className="grid gap-0.5">
                              {children.map((child, childIndex) => (
                                <a
                                  key={child.href}
                                  href={buildHref(child.href)}
                                  role="menuitem"
                                  style={{ animationDelay: `${childIndex * 26}ms` }}
                                  className={cn(
                                    'group/item flex items-center gap-3 rounded-xl px-2.5 py-2 outline-none transition hover:bg-slate-50 focus:bg-slate-50',
                                    openIndex === index && 'motion-safe:animate-[nav-rise_.34s_cubic-bezier(.2,.8,.3,1)_backwards]'
                                  )}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') setOpenIndex(null);
                                  }}
                                >
                                  <span className={cn(
                                    'grid h-8 w-8 flex-none place-items-center rounded-[9px] transition-transform group-hover/item:scale-[1.06]',
                                    NAV_TINTS[childIndex % NAV_TINTS.length]
                                  )}>
                                    <NavIcon name={(child as any).icon} className="h-4 w-4" />
                                  </span>
                                  <span className="text-sm font-semibold leading-tight text-slate-700">{child.label}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ul className="max-h-[60vh] overflow-auto py-1">
                        {((item as any).children as Array<{ label: string; href: string }>).map((child) => (
                          <li key={child.href}>
                            <a
                              href={buildHref(child.href)}
                              role="menuitem"
                              className="block rounded-xl px-3 py-2 text-sm text-slate-700 outline-none transition hover:bg-brand-50 hover:text-brand-700 focus:bg-brand-50 focus:text-brand-700"
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') setOpenIndex(null);
                              }}
                            >
                              {child.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link
              href={`/${locale}/search/`}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-brand-700"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </Link>
            <LanguageSwitcher currentLocale={locale} />
            <a
              href="https://dash.winkelfactuur.nl/login"
              className="text-sm font-medium text-slate-700 transition hover:text-brand-700"
              data-track="Header Login - Desktop"
              data-track-category="Login"
            >
              {header.loginLabel}
            </a>
            <a
              href="https://dash.winkelfactuur.nl/register"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-cta px-4 py-2 text-sm font-semibold text-ink shadow-[0_4px_12px_rgba(244,115,14,0.25)] transition-transform hover:scale-[1.03]"
              aria-label={header.ctaLabel}
              data-track="Header CTA - Desktop"
              data-track-category="CTA"
            >
              {header.ctaLabel}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>

        {/* Mobile Layout - Logo on top, CTA buttons below */}
        <div className="lg:hidden">
          {/* Top row: Logo + Menu button */}
          <div className="flex items-center justify-between">
            <Link
              href={homeHref(locale)}
              className="flex items-center gap-3 text-base font-semibold text-slate-900"
              aria-label="Winkel Factuur startpagina"
            >
              <BrandLogo variant="full" className="is-compact" />
            </Link>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full border border-slate-200 p-2 text-slate-700 shadow-sm transition hover:border-brand-400 hover:text-brand-700"
              onClick={() => setIsOpen((prev) => !prev)}
              aria-label={isOpen ? 'Sluit navigatiemenu' : 'Open navigatiemenu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>

          {/* CTA Buttons below logo - with stunning animation */}
          <div className="mt-3 flex items-center justify-center gap-3">
            <a
              href="https://dash.winkelfactuur.nl/login"
              className="group relative inline-flex items-center justify-center rounded-full border-2 border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all duration-300 hover:border-brand-400 hover:text-brand-700 hover:shadow-md active:scale-95"
              data-track="Header Login - Mobile Hero"
              data-track-category="Login"
            >
              <LogIn className="h-4 w-4 mr-2" aria-hidden="true" />
              <span>{header.loginLabel}</span>
            </a>
            <a
              href="https://dash.winkelfactuur.nl/register"
              className="group relative inline-flex items-center justify-center overflow-hidden rounded-full bg-gradient-cta px-5 py-2.5 text-sm font-bold text-ink shadow-[0_4px_12px_rgba(244,115,14,0.25)] transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
              data-track="Header CTA - Mobile Hero"
              data-track-category="CTA"
            >
              <span className="relative flex items-center gap-2">
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                <span>{header.ctaLabel}</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </a>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="lg:hidden overflow-hidden border-t border-slate-100 animate-fade-in-up">
          <div className="container-wide space-y-4 pb-6 pt-2 max-h-[calc(100vh-160px)] overflow-y-auto overscroll-contain">
          <nav className="flex flex-col gap-2" aria-label="Mobiele navigatie">
            {navigation.map((item) => (
              <div key={item.href}>
                <a
                  href={buildHref(item.href)}
                  className="block rounded-lg px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-brand-50 hover:text-brand-700"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
                {Array.isArray((item as any).children) && (item as any).children.length > 0 && (
                  <div className="ml-4 mt-1 space-y-2">
                    {groupChildren(item).map(([group, children]) => (
                      <div key={group}>
                        {group && (
                          <p className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{group}</p>
                        )}
                        {children.map((child, childIndex) => (
                          <a
                            key={child.href}
                            href={buildHref(child.href)}
                            className="flex items-center gap-2.5 rounded-md px-3 py-1.5 text-xs text-slate-700 transition hover:bg-brand-50 hover:text-brand-700"
                            onClick={() => setIsOpen(false)}
                          >
                            <span className={cn(
                              'grid h-6 w-6 flex-none place-items-center rounded-md',
                              NAV_TINTS[childIndex % NAV_TINTS.length]
                            )}>
                              <NavIcon name={child.icon} className="h-3.5 w-3.5" />
                            </span>
                            {child.label}
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <LanguageSwitcher currentLocale={locale} className="w-full" variant="list" />
          </div>
        </div>
      )}
    </header>
  );
}
